import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Activity,
  ClipboardList,
  DollarSign,
  CheckCircle2,
  Dumbbell,
  Trophy,
  AlertTriangle,
  Clock,
  Calendar,
  MessageSquare,
  Sparkles,
  MoreHorizontal,
} from 'lucide-react';
import { ProTopBar } from '../components/ProTopBar';
import { useAuth } from '../lib/auth';
import { useRole } from '../lib/role';
import { usePatients, useAthletes, useCoachAlerts, useCoachFeed, useTopPR, useVolumeWeek, useRecoveryHeatmap, useCoachMe } from '../lib/queries';
import type { AthleteRow, AthleteStatus, CoachAlertKind, FeedKind } from '../lib/api';
import styles from './Dashboard.module.css';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Color hash deterministico per avatar (matches design palette). */
function avatarColor(seed: string): string {
  const palette = ['#1d4ed8', '#7c3aed', '#0b8a3e', '#e85d00', '#0891b2', '#d64545', '#be185d', '#a87c00'];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
}

function todayLabelIt(): string {
  const days = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
  const months = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
  const d = new Date();
  return `${days[d.getDay()]} · ${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`.toUpperCase();
}

function timeAgoIt(iso: string | null): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMin = Math.floor((now - then) / 60000);
  if (diffMin < 1) return 'ora';
  if (diffMin < 60) return `${diffMin}m fa`;
  if (diffMin < 60 * 24) return `${Math.floor(diffMin / 60)}h fa`;
  const days = Math.floor(diffMin / 60 / 24);
  if (days === 1) return 'ieri';
  if (days < 8) return `${days}g fa`;
  return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
}

function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
}

// ---------------------------------------------------------------------------
// KPI card
// ---------------------------------------------------------------------------

interface KpiProps {
  label: string;
  value: string;
  unit?: string;
  deltaText?: string;
  deltaVariant?: 'up' | 'down' | 'neutral';
  iconBg: string;
  iconColor: string;
  icon: React.ReactNode;
}

function Kpi({ label, value, unit, deltaText, deltaVariant = 'neutral', iconBg, iconColor, icon }: KpiProps) {
  const dVar =
    deltaVariant === 'up' ? styles.kpiDeltaUp :
    deltaVariant === 'down' ? styles.kpiDeltaDown :
    styles.kpiDeltaNeutral;

  return (
    <div className={styles.kpi}>
      <div className={styles.kpiIconBg} style={{ background: iconBg, color: iconColor }}>
        {icon}
      </div>
      <div className={styles.kpiLabel}>{label}</div>
      <div className={`${styles.kpiVal} tnum`}>
        {value}
        {unit && <span className={styles.kpiUnit}>{unit}</span>}
      </div>
      {deltaText && <span className={`${styles.kpiDelta} ${dVar}`}>{deltaText}</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hero block — role-aware
// ---------------------------------------------------------------------------

function Hero() {
  const { user } = useAuth();
  const { role } = useRole();
  const [period, setPeriod] = useState<'today' | 'week' | 'month' | 'quarter'>('today');
  const firstName = (user?.name || '').split(/\s+/)[0] || 'Pro';

  return (
    <div className={styles.hero}>
      <div>
        <div className={styles.heroDate}>{todayLabelIt()}</div>
        {role === 'nutri' ? (
          <h1 className={styles.heroTitle}>Buongiorno, {firstName}<em>.</em></h1>
        ) : (
          <h1 className={styles.heroTitle}>Coach<em>, eccoti.</em></h1>
        )}
      </div>
      <div className={styles.heroActions}>
        {(['today', 'week', 'month', 'quarter'] as const).map((p) => (
          <button
            key={p}
            type="button"
            className={`${styles.pillBtn} ${period === p ? styles.pillBtnActive : ''}`}
            onClick={() => setPeriod(p)}
          >
            {p === 'today' ? 'Oggi' : p === 'week' ? '7 giorni' : p === 'month' ? '30 giorni' : 'Trimestre'}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// KPI strip — role-aware
// ---------------------------------------------------------------------------

function NutriKpis() {
  const { data: patients } = usePatients();
  const list = patients || [];
  const total = list.length;
  const active = list.filter((p) => p.last_check && (daysSince(p.last_check) ?? 99) <= 7).length;
  const inReview = list.filter((p) => (p.mods_7d || 0) > 0).length;

  return (
    <div className={styles.kpis}>
      <Kpi
        label="Pazienti attivi"
        value={String(total)}
        deltaText={total > 0 ? `${active} attivi · 7gg` : undefined}
        deltaVariant="up"
        iconBg="var(--green-soft)"
        iconColor="var(--green-deep)"
        icon={<Users size={16} strokeWidth={2} />}
      />
      <Kpi
        label="Aderenza media · 30g"
        value="—"
        deltaText="In arrivo Fase backend"
        deltaVariant="neutral"
        iconBg="var(--orange-soft)"
        iconColor="var(--orange-deep)"
        icon={<Activity size={16} strokeWidth={2} />}
      />
      <Kpi
        label="Piani in revisione"
        value={String(inReview)}
        deltaText={inReview > 0 ? 'Aperti' : 'Nessuno'}
        deltaVariant={inReview > 0 ? 'down' : 'neutral'}
        iconBg="#fff3d6"
        iconColor="#a87c00"
        icon={<ClipboardList size={16} strokeWidth={2} />}
      />
      <Kpi
        label="Fatturato mese"
        value="—"
        deltaText="Sprint billing"
        deltaVariant="neutral"
        iconBg="var(--blue-soft)"
        iconColor="var(--blue-deep)"
        icon={<DollarSign size={16} strokeWidth={2} />}
      />
    </div>
  );
}

function CoachKpis() {
  const { data: athletes } = useAthletes(true);
  const { data: me } = useCoachMe(true);
  const { data: volume } = useVolumeWeek(true);

  const list = athletes || [];
  const liveCount = list.filter((a) => a.status === 'live').length;
  const newCount = list.filter((a) => {
    const d = daysSince(a.last_session_at);
    return d !== null && d <= 7;
  }).length;
  const prMonth = list.reduce((sum, a) => sum + (a.pr_30d || 0), 0);
  const volumeKg = (volume?.points || []).reduce((s, p) => s + (p.kg_total || 0), 0);
  const volumeT = volumeKg > 0 ? (volumeKg / 1000).toFixed(1) : '0';

  // adherence: media non-null
  const adhValues = list.map((a) => a.adherence_7d).filter((x): x is number => x !== null);
  const adhAvg = adhValues.length > 0
    ? Math.round(adhValues.reduce((s, n) => s + n, 0) / adhValues.length)
    : null;

  return (
    <div className={styles.kpis}>
      <Kpi
        label="Atleti attivi"
        value={String(me?.athletes_count ?? list.length)}
        deltaText={liveCount > 0 ? `${liveCount} in sessione ora` : `${newCount} attivi · 7gg`}
        deltaVariant="up"
        iconBg="var(--orange-soft)"
        iconColor="var(--orange-deep)"
        icon={<Users size={16} strokeWidth={2} />}
      />
      <Kpi
        label="Aderenza programma · 7g"
        value={adhAvg !== null ? String(adhAvg) : '—'}
        unit={adhAvg !== null ? '%' : undefined}
        deltaText={adhAvg !== null ? 'Media studio' : 'Nessun dato'}
        deltaVariant={adhAvg !== null && adhAvg >= 80 ? 'up' : 'neutral'}
        iconBg="var(--green-soft)"
        iconColor="var(--green-deep)"
        icon={<CheckCircle2 size={16} strokeWidth={2} />}
      />
      <Kpi
        label="Volume settimana"
        value={volumeT}
        unit="t"
        deltaText="Tonnellaggio totale"
        deltaVariant="neutral"
        iconBg="#fff3d6"
        iconColor="#a87c00"
        icon={<Dumbbell size={16} strokeWidth={2} />}
      />
      <Kpi
        label="PR del mese"
        value={String(prMonth)}
        deltaText={prMonth > 0 ? 'Sbloccati' : 'In attesa'}
        deltaVariant={prMonth > 0 ? 'up' : 'neutral'}
        iconBg="var(--purple-soft)"
        iconColor="var(--purple)"
        icon={<Trophy size={16} strokeWidth={2} />}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Nutri roster
// ---------------------------------------------------------------------------

function NutriRoster() {
  const { data: patients, isLoading, isError } = usePatients();
  const top = (patients || []).slice(0, 7);

  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <div>
          <div className={styles.cardTitle}>Pazienti — attività recente</div>
          <div className={styles.cardSub}>{patients?.length ?? 0} attivi · ordinati per ultima sincronizzazione</div>
        </div>
      </header>
      {isError ? (
        <div className={styles.errorBox}>Errore caricamento pazienti.</div>
      ) : isLoading ? (
        <div className={styles.empty}>Caricamento…</div>
      ) : top.length === 0 ? (
        <div className={styles.empty}>Nessun paziente ancora. Aggiungi il primo dal pulsante in alto.</div>
      ) : (
        <div className={styles.roster}>
          <table className={styles.rosterTable}>
            <thead>
              <tr>
                <th style={{ width: '30%' }}>Paziente</th>
                <th>Stato</th>
                <th>Aderenza 7g</th>
                <th>Peso</th>
                <th>Δ 30g</th>
                <th>Ultimo contatto</th>
                <th style={{ width: 40 }} aria-label="Azioni" />
              </tr>
            </thead>
            <tbody>
              {top.map((p) => {
                const adh = p.adherence_7d_pct;
                const status = p.status || 'active';
                const lastDays = daysSince(p.last_check);
                const statusClass =
                  status === 'attention' ? styles.statusAttention :
                  status === 'risk' ? styles.statusPaused :
                  styles.statusActive;
                const statusDotBg =
                  status === 'attention' ? 'var(--orange)' :
                  status === 'risk' ? 'var(--mute-2)' :
                  'var(--green)';
                const statusLabel =
                  status === 'attention' ? 'ATTENZIONE' :
                  status === 'risk' ? 'IN PAUSA' :
                  'ATTIVO';

                return (
                  <tr key={p.id}>
                    <td>
                      <div className={styles.pt}>
                        <div className={styles.ptAv} style={{ background: avatarColor(p.id) }}>
                          {initials(p.name)}
                        </div>
                        <div>
                          <div className={styles.ptName}>{p.name}</div>
                          <div className={styles.ptMeta}>{p.human_id || '—'}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`${styles.statusBadge} ${statusClass}`}>
                        <span className={styles.statusDot} style={{ background: statusDotBg }} />
                        {statusLabel}
                      </span>
                    </td>
                    <td>
                      <div className={styles.adhCell}>
                        <span className={styles.num}>{adh !== null && adh !== undefined ? `${adh}%` : '—'}</span>
                        <div className={styles.adhBar}>
                          {adh !== null && adh !== undefined && (
                            <div style={{ width: `${adh}%`, background: adh >= 80 ? 'var(--green)' : adh >= 60 ? 'var(--orange)' : 'var(--red)' }} />
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={styles.num}>
                        {p.weight_kg !== null && p.weight_kg !== undefined ? `${p.weight_kg.toFixed(1)} kg` : '—'}
                      </span>
                    </td>
                    <td>
                      <span className={`${styles.num} ${p.weight_delta_kg && p.weight_delta_kg < 0 ? styles.trendDown : p.weight_delta_kg && p.weight_delta_kg > 0 ? styles.trendUp : styles.numMute}`}>
                        {p.weight_delta_kg !== null && p.weight_delta_kg !== undefined
                          ? `${p.weight_delta_kg > 0 ? '+' : ''}${p.weight_delta_kg.toFixed(1)}`
                          : '—'}
                      </span>
                    </td>
                    <td>
                      <span className={`${styles.num} ${styles.numMute}`}>
                        {lastDays === null ? '—' : lastDays === 0 ? 'oggi' : lastDays === 1 ? 'ieri' : `${lastDays}g fa`}
                      </span>
                    </td>
                    <td>
                      <button type="button" className={styles.rowAction} aria-label="Altre azioni">
                        <MoreHorizontal size={16} strokeWidth={2} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Coach roster
// ---------------------------------------------------------------------------

function statusToBadge(s: AthleteStatus): { className: string; dotBg: string; label: string } {
  switch (s) {
    case 'live':
      return { className: styles.statusLive, dotBg: 'var(--green)', label: 'LIVE' };
    case 'attention':
      return { className: styles.statusAttention, dotBg: 'var(--orange)', label: 'ATTENZIONE' };
    case 'deload':
      return { className: styles.statusDeload, dotBg: 'var(--purple)', label: 'DELOAD' };
    case 'active':
    default:
      return { className: styles.statusActive, dotBg: 'var(--green)', label: 'ATTIVO' };
  }
}

function CoachRoster() {
  const { data: athletes, isLoading, isError } = useAthletes(true);
  const top = (athletes || []).slice(0, 7);

  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <div>
          <div className={styles.cardTitle}>Atleti — attività recente</div>
          <div className={styles.cardSub}>{athletes?.length ?? 0} attivi · ordinati per ultima sessione</div>
        </div>
      </header>
      {isError ? (
        <div className={styles.errorBox}>Errore caricamento atleti.</div>
      ) : isLoading ? (
        <div className={styles.empty}>Caricamento…</div>
      ) : top.length === 0 ? (
        <div className={styles.empty}>Nessun atleta agganciato. Crea il primo link nutrizionista-coach.</div>
      ) : (
        <div className={styles.roster}>
          <table className={styles.rosterTable}>
            <thead>
              <tr>
                <th style={{ width: '28%' }}>Atleta</th>
                <th>Programma</th>
                <th>Aderenza 7g</th>
                <th>Volume sett.</th>
                <th>PR 30g</th>
                <th>Ultima</th>
                <th style={{ width: 40 }} aria-label="Azioni" />
              </tr>
            </thead>
            <tbody>
              {top.map((a: AthleteRow) => {
                const b = statusToBadge(a.status);
                const adh = a.adherence_7d;
                const lastLabel = a.status === 'live' ? 'ora' : timeAgoIt(a.last_session_at);
                const lastClass = a.status === 'live' ? styles.numLive : styles.numMute;

                return (
                  <tr key={a.id}>
                    <td>
                      <div className={styles.pt}>
                        <div className={styles.ptAv} style={{ background: avatarColor(a.id) }}>
                          {initials(a.name)}
                        </div>
                        <div>
                          <div className={styles.ptName}>{a.name}</div>
                          <div className={styles.ptMeta}>
                            {[a.sex, a.age ? `${a.age}a` : null, a.goal].filter(Boolean).join(' · ') || '—'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`${styles.statusBadge} ${b.className}`}>
                        <span className={styles.statusDot} style={{ background: b.dotBg }} />
                        {b.label}{a.program_name ? ` · ${a.program_name.toUpperCase()}` : ''}
                      </span>
                    </td>
                    <td>
                      <div className={styles.adhCell}>
                        <span className={styles.num}>{adh !== null ? `${adh}%` : '—'}</span>
                        <div className={styles.adhBar}>
                          {adh !== null && (
                            <div style={{ width: `${adh}%`, background: adh >= 80 ? 'var(--green)' : adh >= 60 ? 'var(--orange)' : 'var(--red)' }} />
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={styles.num}>
                        {a.volume_week_kg !== null
                          ? a.volume_week_kg >= 1000
                            ? `${(a.volume_week_kg / 1000).toFixed(1)} t`
                            : `${a.volume_week_kg} kg`
                          : '—'}
                      </span>
                    </td>
                    <td>
                      <span className={`${styles.num} ${a.pr_30d > 0 ? styles.trendUp : styles.numMute}`}>
                        {a.pr_30d > 0 ? `+${a.pr_30d}` : '0'}
                      </span>
                    </td>
                    <td>
                      <span className={`${styles.num} ${lastClass}`}>{lastLabel}</span>
                    </td>
                    <td>
                      <button type="button" className={styles.rowAction} aria-label="Altre azioni">
                        <MoreHorizontal size={16} strokeWidth={2} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Coach live sessions
// ---------------------------------------------------------------------------

function CoachLiveSessions() {
  const { data: athletes } = useAthletes(true);
  const liveOnes = (athletes || []).filter((a) => a.status === 'live');

  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <div>
          <div className={styles.cardTitle}>Sessioni live</div>
          <div className={styles.cardSub}>
            {liveOnes.length} atleti in palestra · monitoraggio in tempo reale
          </div>
        </div>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            fontFamily: 'var(--mono)',
            fontSize: 10,
            fontWeight: 600,
            color: 'var(--green-deep)',
            letterSpacing: 0.6,
            background: 'var(--green-soft)',
            padding: '3px 8px',
            borderRadius: 999,
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: 3,
              background: 'var(--green)',
              boxShadow: '0 0 0 3px rgba(11,138,62,.18)',
            }}
          />
          LIVE
        </span>
      </header>
      <div className={styles.liveAthletes}>
        {liveOnes.length === 0 ? (
          <div className={styles.empty}>Nessuna sessione live in corso.</div>
        ) : (
          liveOnes.map((a) => (
            <div key={a.id} className={styles.liveRow}>
              <div className={styles.liveAv} style={{ background: avatarColor(a.id) }}>
                {initials(a.name)}
              </div>
              <div>
                <div className={styles.ptName}>{a.name}</div>
                <div className={styles.liveCur}>
                  {a.program_name || 'Programma'} · in sessione
                </div>
              </div>
              <div className={styles.liveProg}>
                <div className={styles.adhBar}>
                  <div style={{ width: `${a.adherence_7d ?? 0}%`, background: 'var(--orange)' }} />
                </div>
                <div className={styles.liveProgLabel}>
                  {a.adherence_7d !== null ? `${a.adherence_7d}%` : '—'}
                </div>
              </div>
              <div className={styles.liveBpm}>
                <div className={`${styles.liveBpmNum} tnum`}>—</div>
                <div className={styles.liveBpmLbl}>BPM</div>
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Coach volume chart (7-day)
// ---------------------------------------------------------------------------

function CoachVolumeChart() {
  const { data, isLoading } = useVolumeWeek(true);
  const points = data?.points || [];
  const max = points.reduce((m, p) => Math.max(m, p.kg_total), 1); // avoid /0

  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <div>
          <div className={styles.cardTitle}>Volume settimanale</div>
          <div className={styles.cardSub}>Tonnellaggio per giorno · ultimi 7gg</div>
        </div>
      </header>
      {isLoading ? (
        <div className={styles.empty}>Caricamento…</div>
      ) : (
        <div className={styles.barChart}>
          {points.map((p) => {
            const heightPct = max > 0 ? (p.kg_total / max) * 100 : 0;
            return (
              <div key={p.iso_date} className={styles.barCol}>
                <span className={`${styles.barValue} tnum`}>
                  {p.kg_total > 0 ? (p.kg_total >= 1000 ? `${(p.kg_total / 1000).toFixed(1)}t` : `${p.kg_total}kg`) : '—'}
                </span>
                <div
                  className={styles.barFill}
                  style={{ height: `max(4px, ${heightPct}%)` }}
                  title={`${p.day_label} · ${p.kg_total}kg`}
                />
                <span className={styles.barLabel}>{p.day_label}</span>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Coach alerts
// ---------------------------------------------------------------------------

const ALERT_KIND_META: Record<CoachAlertKind, { icon: React.ReactNode; bg: string; color: string }> = {
  missed: {
    icon: <AlertTriangle size={14} strokeWidth={2.2} />,
    bg: '#fbe5e5',
    color: 'var(--red)',
  },
  overtrain: {
    icon: <Activity size={14} strokeWidth={2.2} />,
    bg: '#fff3d6',
    color: '#a87c00',
  },
  'deload-due': {
    icon: <Clock size={14} strokeWidth={2.2} />,
    bg: 'var(--purple-soft)',
    color: 'var(--purple)',
  },
};

function CoachAlertsCard() {
  const { data, isLoading } = useCoachAlerts(true);
  const alerts = data?.alerts || [];

  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <div>
          <div className={styles.cardTitle}>Richiedono attenzione</div>
          <div className={styles.cardSub}>{alerts.length} segnali · ordinati per priorità</div>
        </div>
        {alerts.length > 0 && (
          <span
            style={{
              background: 'var(--orange)',
              color: 'var(--ink)',
              fontFamily: 'var(--mono)',
              fontSize: 10,
              fontWeight: 700,
              padding: '3px 7px',
              borderRadius: 5,
              letterSpacing: 0.5,
            }}
          >
            {alerts.length}
          </span>
        )}
      </header>
      <div style={{ padding: '8px 16px 16px' }}>
        {isLoading ? (
          <div className={styles.empty}>Caricamento…</div>
        ) : alerts.length === 0 ? (
          <div className={styles.empty}>Nessun segnale critico.</div>
        ) : (
          alerts.map((a, i) => {
            const meta = ALERT_KIND_META[a.kind] || ALERT_KIND_META.missed;
            return (
              <div key={`${a.kind}-${a.athlete_id}-${i}`} className={styles.alertRow}>
                <div className={styles.alertIcon} style={{ background: meta.bg, color: meta.color }}>
                  {meta.icon}
                </div>
                <div>
                  <div className={styles.alertTitle}>{a.name}</div>
                  <div className={styles.alertMeta}>{a.message}</div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Coach feed (PR + sessions + notes)
// ---------------------------------------------------------------------------

const FEED_KIND_META: Record<FeedKind, { icon: React.ReactNode; bg: string; color: string }> = {
  pr: {
    icon: <Trophy size={14} strokeWidth={2} />,
    bg: '#fff3d6',
    color: '#a87c00',
  },
  session_done: {
    icon: <CheckCircle2 size={14} strokeWidth={2.4} />,
    bg: 'var(--green-soft)',
    color: 'var(--green-deep)',
  },
  note: {
    icon: <MessageSquare size={14} strokeWidth={2} />,
    bg: 'var(--blue-soft)',
    color: 'var(--blue-deep)',
  },
};

function CoachFeedCard() {
  const { data, isLoading } = useCoachFeed(true);
  const items = data?.feed || [];

  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <div>
          <div className={styles.cardTitle}>PR &amp; sessioni live</div>
          <div className={styles.cardSub}>Ultimi record e completamenti</div>
        </div>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            fontFamily: 'var(--mono)',
            fontSize: 10,
            fontWeight: 600,
            color: 'var(--green-deep)',
            letterSpacing: 0.6,
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: 3,
              background: 'var(--green)',
              boxShadow: '0 0 0 3px rgba(11,138,62,.18)',
            }}
          />
          LIVE
        </span>
      </header>
      <div className={styles.feed}>
        {isLoading ? (
          <div className={styles.empty}>Caricamento…</div>
        ) : items.length === 0 ? (
          <div className={styles.empty}>Ancora nessuna attività registrata.</div>
        ) : (
          items.slice(0, 8).map((f, i) => {
            const meta = FEED_KIND_META[f.kind] || FEED_KIND_META.session_done;
            return (
              <div key={`${f.kind}-${f.athlete_id}-${i}`} className={styles.feedItem}>
                <div className={styles.feedDot} style={{ background: meta.bg, color: meta.color }}>
                  {meta.icon}
                </div>
                <div className={styles.feedBody}>
                  <div className={styles.feedText}>
                    <b>{f.name}</b> · {f.message}
                  </div>
                  <div className={styles.feedTime}>{timeAgoIt(f.created_at).toUpperCase()}</div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Recovery heatmap (coach)
// ---------------------------------------------------------------------------

function heatmapBg(score: number | null): string {
  if (score === null) return 'var(--line-3)';
  // gradient: red→yellow→green (40..100)
  if (score < 50) return '#edb6b6';
  if (score < 60) return '#e8d5a3';
  if (score < 70) return '#cce8d4';
  if (score < 80) return '#aedcc0';
  if (score < 90) return '#94d2a8';
  return '#7bc998';
}

function CoachRecoveryHeatmap() {
  const { data, isLoading } = useRecoveryHeatmap(7, true);
  const athletes = data?.athletes || [];

  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <div>
          <div className={styles.cardTitle}>Recupero · 7 giorni</div>
          <div className={styles.cardSub}>Score basato su volume e RPE · alto = recuperato</div>
        </div>
      </header>
      <div className={styles.heatmap}>
        {isLoading ? (
          <div className={styles.empty}>Caricamento…</div>
        ) : athletes.length === 0 ? (
          <div className={styles.empty}>Nessun dato di recupero disponibile.</div>
        ) : (
          <>
            <div className={styles.heatmapHead}>
              <div>ATLETA</div>
              {athletes[0].days.map((d) => {
                const date = new Date(d.date + 'T12:00:00Z');
                const dow = ['DOM', 'LUN', 'MAR', 'MER', 'GIO', 'VEN', 'SAB'][date.getUTCDay()];
                return <div key={d.date}>{dow}</div>;
              })}
            </div>
            {athletes.slice(0, 6).map((a) => (
              <div key={a.athlete_id} className={styles.heatmapRow}>
                <div className={styles.heatmapLbl}>
                  <div className={styles.ptAv} style={{ background: avatarColor(a.athlete_id) }}>
                    {initials(a.name)}
                  </div>
                  {a.name.split(/\s+/)[0]} {a.name.split(/\s+/)[1]?.[0] ? `${a.name.split(/\s+/)[1][0]}.` : ''}
                </div>
                {a.days.map((d) => (
                  <div
                    key={d.date}
                    className={`${styles.heatmapCell} ${d.score === null ? styles.heatmapCellEmpty : ''}`}
                    style={{ background: heatmapBg(d.score) }}
                  >
                    {d.score ?? ''}
                  </div>
                ))}
              </div>
            ))}
          </>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Coach top PR
// ---------------------------------------------------------------------------

function CoachTopPR() {
  const { data, isLoading } = useTopPR(30, true);
  const records = data?.records || [];

  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <div>
          <div className={styles.cardTitle}>Top PR · 30 giorni</div>
          <div className={styles.cardSub}>{records.length} record · ordinati per data</div>
        </div>
      </header>
      <div className={styles.prList}>
        {isLoading ? (
          <div className={styles.empty}>Caricamento…</div>
        ) : records.length === 0 ? (
          <div className={styles.empty}>Nessun PR negli ultimi 30 giorni.</div>
        ) : (
          records.slice(0, 5).map((r, i) => (
            <div key={`${r.athlete_id}-${r.exercise_name}-${i}`} className={styles.prRow}>
              <div className={styles.prRank}>{String(i + 1).padStart(2, '0')}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div className={styles.ptAv} style={{ background: avatarColor(r.athlete_id), width: 28, height: 28, fontSize: 10 }}>
                  {initials(r.name)}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{r.name}</div>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--mute)', marginTop: 1, textTransform: 'uppercase' }}>
                    {r.exercise_name} · {r.kind.toUpperCase()}
                  </div>
                </div>
              </div>
              <div className={styles.prValue}>
                {r.value_kg !== null ? r.value_kg : r.value_reps || '—'}
                <span className={styles.prValueUnit}>{r.value_kg !== null ? 'kg' : r.value_reps ? 'reps' : ''}</span>
              </div>
              <span className={styles.prDelta}>PR</span>
            </div>
          ))
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Nutri agenda placeholder (real Calendar module Sprint successive)
// ---------------------------------------------------------------------------

function NutriAgenda() {
  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <div>
          <div className={styles.cardTitle}>Agenda di oggi</div>
          <div className={styles.cardSub}>Modulo agenda in arrivo</div>
        </div>
        <Calendar size={16} strokeWidth={1.8} style={{ color: 'var(--mute)' }} />
      </header>
      <div className={styles.empty}>
        Il modulo agenda è in via di sviluppo. Nel frattempo, gestisci appuntamenti su Google Calendar.
      </div>
    </section>
  );
}

function NutriAdherenceCard() {
  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <div>
          <div className={styles.cardTitle}>Aderenza menu · 30g</div>
          <div className={styles.cardSub}>In arrivo: trend studio + segmenti</div>
        </div>
      </header>
      <div className={styles.empty}>
        Grafico aderenza disponibile dopo l'attivazione del backend Fase 4 analytics.
      </div>
    </section>
  );
}

function NutriAlertsCard() {
  const { data: patients } = usePatients();
  const attention = (patients || []).filter((p) => p.status === 'attention' || p.status === 'risk');

  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <div>
          <div className={styles.cardTitle}>Richiedono attenzione</div>
          <div className={styles.cardSub}>{attention.length} segnali</div>
        </div>
        {attention.length > 0 && (
          <span
            style={{
              background: 'var(--orange)',
              color: 'var(--ink)',
              fontFamily: 'var(--mono)',
              fontSize: 10,
              fontWeight: 700,
              padding: '3px 7px',
              borderRadius: 5,
            }}
          >
            {attention.length}
          </span>
        )}
      </header>
      <div style={{ padding: '8px 16px 16px' }}>
        {attention.length === 0 ? (
          <div className={styles.empty}>Tutto sotto controllo.</div>
        ) : (
          attention.slice(0, 5).map((p) => (
            <div key={p.id} className={styles.alertRow}>
              <div className={styles.alertIcon} style={{ background: 'var(--orange-soft)', color: 'var(--orange-deep)' }}>
                <AlertTriangle size={14} strokeWidth={2.2} />
              </div>
              <div>
                <div className={styles.alertTitle}>{p.name}</div>
                <div className={styles.alertMeta}>
                  {p.last_check
                    ? `Ultimo accesso ${daysSince(p.last_check)}g fa`
                    : 'Nessun accesso recente'}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
}

function NutriFeedCard() {
  const { data: patients } = usePatients();
  const activity = useMemo(() => {
    const list = patients || [];
    return list
      .filter((p) => p.last_check)
      .sort((a, b) => (b.last_check || '').localeCompare(a.last_check || ''))
      .slice(0, 6);
  }, [patients]);

  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <div>
          <div className={styles.cardTitle}>Attività live</div>
          <div className={styles.cardSub}>In tempo reale dai pazienti</div>
        </div>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            fontFamily: 'var(--mono)',
            fontSize: 10,
            fontWeight: 600,
            color: 'var(--green-deep)',
            letterSpacing: 0.6,
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: 3,
              background: 'var(--green)',
              boxShadow: '0 0 0 3px rgba(11,138,62,.18)',
            }}
          />
          LIVE
        </span>
      </header>
      <div className={styles.feed}>
        {activity.length === 0 ? (
          <div className={styles.empty}>Nessuna attività recente.</div>
        ) : (
          activity.map((p) => (
            <div key={p.id} className={styles.feedItem}>
              <div className={styles.feedDot} style={{ background: 'var(--green-soft)', color: 'var(--green-deep)' }}>
                <CheckCircle2 size={14} strokeWidth={2.4} />
              </div>
              <div className={styles.feedBody}>
                <div className={styles.feedText}>
                  <b>{p.name}</b> · {(p.mods_7d || 0) > 0
                    ? `${p.mods_7d} modifiche ultime 7gg`
                    : 'check-in registrato'}
                </div>
                <div className={styles.feedTime}>{timeAgoIt(p.last_check).toUpperCase()}</div>
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
}

function NutriDemographics() {
  const { data: patients } = usePatients();
  const list = patients || [];
  const withPlan = list.filter((p) => p.plan_count > 0).length;
  const withoutPlan = list.length - withPlan;

  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <div>
          <div className={styles.cardTitle}>Composizione studio</div>
          <div className={styles.cardSub}>{list.length} pazienti</div>
        </div>
      </header>
      <div style={{ padding: '16px 20px 20px' }}>
        <div style={{ marginBottom: 18 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontFamily: 'var(--mono)',
              fontSize: 10,
              fontWeight: 600,
              color: 'var(--mute)',
              letterSpacing: 1,
              marginBottom: 6,
            }}
          >
            <span>PIANO ATTIVO</span>
            <span>{list.length} PAZ.</span>
          </div>
          <div style={{ height: 22, background: 'var(--surface-2)', borderRadius: 6, overflow: 'hidden', display: 'flex' }}>
            <div
              style={{
                width: list.length > 0 ? `${(withPlan / list.length) * 100}%` : '0%',
                background: 'var(--green)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                padding: '0 8px',
                fontFamily: 'var(--mono)',
                fontSize: 10.5,
                fontWeight: 600,
              }}
            >
              {withPlan} CON
            </div>
            <div
              style={{
                width: list.length > 0 ? `${(withoutPlan / list.length) * 100}%` : '100%',
                background: 'var(--mute-2)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                padding: '0 8px',
                fontFamily: 'var(--mono)',
                fontSize: 10.5,
                fontWeight: 600,
              }}
            >
              {withoutPlan} SENZA
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function NutriRevenue() {
  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <div>
          <div className={styles.cardTitle}>Ricavi &amp; abbonamenti</div>
          <div className={styles.cardSub}>Maggio 2026 · in corso</div>
        </div>
      </header>
      <div className={styles.empty}>
        Dashboard ricavi disponibile dopo l'integrazione Stripe billing (Sprint E1).
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function Dashboard() {
  const { role } = useRole();
  const navigate = useNavigate();

  return (
    <>
      {/* CTA role-aware: nutri → modale nuovo paziente in /clienti, coach → roster atleti.
          Label di default ("Nuovo paziente"/"Nuovo atleta") gestita da ProTopBar via useRole. */}
      <ProTopBar onCta={() => navigate(role === 'coach' ? '/atleti' : '/clienti?new=1')} />

      <Hero />

      {role === 'nutri' ? <NutriKpis /> : <CoachKpis />}

      {/* Row 1 — Roster + Agenda/Live */}
      <div className={styles.gridMain}>
        {role === 'nutri' ? (
          <>
            <NutriRoster />
            <NutriAgenda />
          </>
        ) : (
          <>
            <CoachRoster />
            <CoachLiveSessions />
          </>
        )}
      </div>

      {/* Row 2 — Trend + Alerts + Feed */}
      <div className={styles.gridSecond}>
        {role === 'nutri' ? (
          <>
            <NutriAdherenceCard />
            <NutriAlertsCard />
            <NutriFeedCard />
          </>
        ) : (
          <>
            <CoachVolumeChart />
            <CoachAlertsCard />
            <CoachFeedCard />
          </>
        )}
      </div>

      {/* Row 3 — Demographics + Revenue / Heatmap + Top PR */}
      <div className={styles.gridThird}>
        {role === 'nutri' ? (
          <>
            <NutriDemographics />
            <NutriRevenue />
          </>
        ) : (
          <>
            <CoachRecoveryHeatmap />
            <CoachTopPR />
          </>
        )}
      </div>

      <div style={{ height: 24 }} />
      <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 24px', color: 'var(--mute)', fontSize: 11, fontFamily: 'var(--mono)', letterSpacing: 0.5 }}>
        <Sparkles size={12} strokeWidth={2} style={{ marginRight: 6, opacity: 0.6 }} />
        Sprint 6.A · Dashboard PRO V2
      </div>
    </>
  );
}
